import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const CouponForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    companyID: '',
    transactionDate: new Date().toISOString().split('T')[0],
    coupons: '',
    unitPrice: '35', // Default Unit Price set to 35
    totalPrice: '',
  });

  const isEdit = !!id;

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchCoupon();
    } else if (location.state?.companyId) {
      // Set company from navigation state when creating new
      setFormData(prev => ({
        ...prev,
        companyID: location.state.companyId
      }));
    }
  }, [id, location.state]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/Companies/active-agencies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies. Please try again.');
    }
  };

  const fetchCoupon = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/Coupons/${id}`);
      const data = response.data;
      setFormData({
        companyID: data.companyID || '',
        transactionDate: data.transactionDate ? new Date(data.transactionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        coupons: data.coupons || '',
        unitPrice: data.unitPrice || '',
        totalPrice: data.totalPrice || '',
      });
    } catch (error) {
      console.error('Error fetching coupon:', error);
      setError('Failed to load coupon data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-calculate total price when coupons or unit price changes
  useEffect(() => {
    if (formData.coupons && formData.unitPrice) {
      const couponsNum = parseFloat(formData.coupons);
      const unitPriceNum = parseFloat(formData.unitPrice);
      if (couponsNum > 0 && unitPriceNum > 0) {
        const total = couponsNum * unitPriceNum;
        setFormData(prev => ({
          ...prev,
          totalPrice: total.toFixed(2)
        }));
      }
    }
  }, [formData.coupons, formData.unitPrice]);

  // Required fields
  const requiredFields = {
    companyID: 'Company is required',
    transactionDate: 'Transaction Date is required',
    coupons: 'Number of Coupons is required',
    unitPrice: 'Unit Price is required',
  };

  const validateField = (field, value) => {
    if (requiredFields[field] && !value) {
      return requiredFields[field];
    }
    // Coupons validation - must be greater than 0
    if (field === 'coupons' && value && parseFloat(value) <= 0) {
      return 'Coupons must be greater than zero';
    }
    // Unit Price validation - must be greater than 0
    if (field === 'unitPrice' && value && parseFloat(value) <= 0) {
      return 'Unit Price must be greater than zero';
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const touched = {};
    let hasErrors = false;

    Object.keys(requiredFields).forEach(field => {
      touched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    setTouchedFields(touched);
    return !hasErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleBlur = (field) => (event) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Validate on blur
    const error = validateField(field, formData[field]);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleCompanyChange = (event, value) => {
    const companyId = value ? value.companyID : '';
    setFormData((prev) => ({
      ...prev,
      companyID: companyId,
    }));
    
    // Clear validation error
    if (validationErrors.companyID) {
      setValidationErrors(prev => ({
        ...prev,
        companyID: null
      }));
    }
    
    // Mark as touched
    setTouchedFields(prev => ({
      ...prev,
      companyID: true
    }));
  };

  const handleSubmit = async () => {
    // Validate all fields and mark them as touched
    const isValid = validateForm();
    
    if (!isValid) {
      // Scroll to the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }, 300);
        }
      }
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const submitData = {
        companyID: formData.companyID,
        transactionDate: formData.transactionDate,
        coupons: parseInt(formData.coupons),
        unitPrice: parseFloat(formData.unitPrice),
        totalPrice: parseFloat(formData.totalPrice),
      };

      if (isEdit) {
        submitData.id = id;
        await api.put(`/Coupons/${id}`, submitData);
      } else {
        await api.post('/Coupons', submitData);
      }

      navigate('/coupons/couponlist');
    } catch (error) {
      console.error('Error saving coupon:', error);
      if (error.response) {
        setError(`Failed to save coupon: ${error.response.data || error.response.statusText}`);
      } else {
        setError('Failed to save coupon. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/coupons/couponlist');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Get selected company object for Autocomplete
  const selectedCompany = companies.find(c => c.companyID === formData.companyID) || null;

  return (
    <Box sx={{ p: 3, maxHeight: 'calc(100vh - 100px)', overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
      <Paper elevation={3} sx={{ p: 3, maxWidth: '800px', width: '100%' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            {isEdit ? 'Edit Coupon' : 'Create New Coupon'}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{ mr: 1 }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Card sx={{ bgcolor: '#fafafa' }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Company - Searchable Autocomplete */}
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth 
                  required
                  error={!!validationErrors.companyID && touchedFields.companyID}
                >
                  <Autocomplete
                    id="field-companyID"
                    options={companies}
                    getOptionLabel={(option) => `${option.companyName} - ${option.country || 'N/A'}`}
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    disabled={isEdit}
                    isOptionEqualToValue={(option, value) => option.companyID === value.companyID}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        required
                        error={!!validationErrors.companyID && touchedFields.companyID}
                        helperText={touchedFields.companyID && validationErrors.companyID}
                        onBlur={() => {
                          setTouchedFields(prev => ({
                            ...prev,
                            companyID: true
                          }));
                          // Validate on blur
                          const error = validateField('companyID', formData.companyID);
                          if (error) {
                            setValidationErrors(prev => ({
                              ...prev,
                              companyID: error
                            }));
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.companyID} value={option.companyID}>
                        {option.companyName} - {option.country || 'N/A'}
                      </MenuItem>
                    )}
                    noOptionsText="No companies found"
                    loadingText="Loading companies..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error': {
                          '& fieldset': {
                            borderColor: '#d32f2f',
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Transaction Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-transactionDate"
                  fullWidth
                  label="Transaction Date"
                  type="date"
                  value={formData.transactionDate}
                  onChange={handleChange('transactionDate')}
                  onBlur={handleBlur('transactionDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!!validationErrors.transactionDate && touchedFields.transactionDate}
                  helperText={touchedFields.transactionDate && validationErrors.transactionDate}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>

              {/* Coupons */}
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-coupons"
                  fullWidth
                  label="Number of Coupons"
                  type="number"
                  value={formData.coupons}
                  onChange={handleChange('coupons')}
                  onBlur={handleBlur('coupons')}
                  required
                  error={!!validationErrors.coupons && touchedFields.coupons}
                  helperText={touchedFields.coupons && validationErrors.coupons}
                  inputProps={{ min: 1, step: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>

              {/* Unit Price */}
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-unitPrice"
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={formData.unitPrice}
                  onChange={handleChange('unitPrice')}
                  onBlur={handleBlur('unitPrice')}
                  required
                  error={!!validationErrors.unitPrice && touchedFields.unitPrice}
                  helperText={touchedFields.unitPrice && validationErrors.unitPrice}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0.01, step: 0.01 }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>

              {/* Total Price (Auto-calculated, Read-only) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Price"
                  type="text"
                  value={formData.totalPrice ? `$${parseFloat(formData.totalPrice).toFixed(2)}` : '$0.00'}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#f0f0f0',
                      '&.Mui-disabled': {
                        backgroundColor: '#f0f0f0',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Info: Auto-calculation message */}
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  * Total Price is automatically calculated when you enter Coupons and Unit Price
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons at bottom */}
        <Box display="flex" justifyContent="center" gap={2} mt={3} pt={2} borderTop={1} borderColor="divider">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update Coupon' : 'Create Coupon'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CouponForm;